import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  backfillRaMappings,
  deleteCityRaMapping,
  listRaCities,
  saveCityRaMapping,
  searchRaAreas,
  setCityEnabled,
} from '../../server/scraper/raCitiesServer';

const { Text, Paragraph } = Typography;

// Status tag for a city's RA mapping.
const MapTag = ({ city }) => {
  if (city.raAreaId) {
    return (
      <Tag color="green">
        mapped → {city.raAreaName || 'RA area'} (#{city.raAreaId})
      </Tag>
    );
  }
  if (city.mappedVia === 'hardcoded') {
    return <Tag color="blue">hardcoded map</Tag>;
  }
  return <Tag color="default">unmapped</Tag>;
};

const RaCitiesTab = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add flow
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [backfilling, setBackfilling] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  // The DB can hold thousands of City docs; default the table to the roster
  // (mapped or enabled) and let the operator reveal the rest.
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listRaCities();
      setCities(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load RA cities:', err);
      message.error('Failed to load cities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedCity = useMemo(
    () => cities.find((c) => c._id === selectedCityId) || null,
    [cities, selectedCityId],
  );

  // When a city is picked, prefill the search box with its name.
  const handlePickCity = (id) => {
    setSelectedCityId(id);
    setCandidates([]);
    setSelectedAreaId(null);
    const c = cities.find((x) => x._id === id);
    setSearchTerm(c?.name || '');
  };

  const handleSearch = async () => {
    const term = (searchTerm || '').trim();
    if (!term) {
      message.warning('Type a city/area name to search RA');
      return;
    }
    setSearching(true);
    setSelectedAreaId(null);
    try {
      const res = await searchRaAreas(term);
      setCandidates(Array.isArray(res) ? res : []);
      if (!res?.length) message.info('No RA areas found for that name');
    } catch (err) {
      console.error('RA search failed:', err);
      message.error('RA area search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSave = async () => {
    if (!selectedCityId) {
      message.warning('Pick a city first');
      return;
    }
    const area = candidates.find((a) => a.id === selectedAreaId);
    if (!area) {
      message.warning('Pick the matching RA area');
      return;
    }
    setSaving(true);
    try {
      await saveCityRaMapping(selectedCityId, {
        raAreaId: area.id,
        raAreaName: area.name,
        raAreaUrlName: area.urlName,
        raCountryUrlCode: area.countryUrlCode,
      });
      message.success(
        `${selectedCity?.name} mapped to ${area.name} and enabled`,
      );
      // Reset add flow
      setSelectedCityId(null);
      setSearchTerm('');
      setCandidates([]);
      setSelectedAreaId(null);
      load();
    } catch (err) {
      console.error('Save mapping failed:', err);
      const detail =
        err?.response?.data?.message ||
        (err?.response
          ? `HTTP ${err.response.status}`
          : err?.message || 'no response (check API base URL / gateway)');
      message.error(`Failed to save mapping — ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (city) => {
    Modal.confirm({
      title: `Remove ${city.name} from the scraper?`,
      content:
        'This clears its RA mapping and disables the city, so it drops out of "All RA-mapped" runs. You can re-add it any time.',
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteCityRaMapping(city._id);
          message.success(`${city.name} removed from the scraper`);
          load();
        } catch (err) {
          console.error('Delete mapping failed:', err);
          message.error('Failed to remove city');
        }
      },
    });
  };

  const handleToggleEnabled = async (city, enabled) => {
    setTogglingId(city._id);
    // Optimistic update.
    setCities((prev) =>
      prev.map((c) => (c._id === city._id ? { ...c, isEnabled: enabled } : c)),
    );
    try {
      await setCityEnabled(city._id, enabled);
      message.success(`${city.name} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Toggle enabled failed:', err);
      // Revert on failure.
      setCities((prev) =>
        prev.map((c) =>
          c._id === city._id ? { ...c, isEnabled: !enabled } : c,
        ),
      );
      const detail =
        err?.response?.data?.message ||
        (err?.response ? `HTTP ${err.response.status}` : err?.message);
      message.error(`Failed to update ${city.name} — ${detail}`);
    } finally {
      setTogglingId(null);
    }
  };

  const handleBackfill = () => {
    Modal.confirm({
      title: 'Import all hardcoded RA mappings?',
      content:
        'Copies the built-in city→RA mappings onto your City docs so the whole roster lives in this list and is editable. Idempotent — skips cities already mapped. Creates/disables nothing else.',
      okText: 'Import',
      cancelText: 'Cancel',
      onOk: async () => {
        setBackfilling(true);
        try {
          const res = await backfillRaMappings(false);
          message.success(
            `Backfill done — ${res.updated} mapped, ${res.alreadyMapped} already, ${res.unmatched} unmatched (of ${res.total})`,
          );
          load();
        } catch (err) {
          console.error('Backfill failed:', err);
          message.error('Backfill failed');
        } finally {
          setBackfilling(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'City',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (n) => <strong>{n}</strong>,
    },
    {
      title: 'RA mapping',
      key: 'mapping',
      render: (_, c) => <MapTag city={c} />,
    },
    {
      title: 'In scraper',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 200,
      filters: [
        { text: 'Enabled', value: true },
        { text: 'Disabled', value: false },
      ],
      onFilter: (v, c) => c.isEnabled === v,
      render: (en, c) => (
        <Space>
          <Switch
            size="small"
            checked={!!c.isEnabled}
            loading={togglingId === c._id}
            onChange={(val) => handleToggleEnabled(c, val)}
          />
          {c.isEnabled && (c.raAreaId || c.mappedVia === 'hardcoded') ? (
            <Tag color="success">in roster</Tag>
          ) : c.isEnabled ? (
            <Tag color="warning">on, unmapped</Tag>
          ) : (
            <Tag color="default">off</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_, c) =>
        c.raAreaId ? (
          <Tooltip title="Clear this city's custom RA mapping and disable it">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(c)}
            >
              Clear map
            </Button>
          </Tooltip>
        ) : null,
    },
  ];

  // Table rows: roster (mapped or enabled) by default; everything when showAll.
  const tableData = useMemo(
    () =>
      showAll
        ? cities
        : cities.filter(
            (c) => c.isEnabled || c.raAreaId || c.mappedVia === 'hardcoded',
          ),
    [cities, showAll],
  );

  const cityOptions = useMemo(
    () =>
      cities.map((c) => ({
        value: c._id,
        label: c.raAreaId
          ? `${c.name} — already mapped`
          : c.mappedVia === 'hardcoded'
            ? `${c.name} — hardcoded`
            : c.name,
      })),
    [cities],
  );

  return (
    <div className="ra-cities-tab">
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="RA Cities — the scraper roster"
        description={
          <span>
            These are the cities the scraper knows. A city is in the{' '}
            <strong>“All RA-mapped”</strong> roster when it’s mapped to an RA area
            and enabled. Add a city by mapping it to its RA area; remove one to
            drop it from runs. Use <strong>Import hardcoded</strong> once to pull
            the built-in mappings into this list.
          </span>
        }
      />

      <Card
        size="small"
        title={
          <Space>
            <PlusOutlined />
            <span>Add / map a city to RA</span>
          </Space>
        }
        className="mb-4"
        extra={
          <Tooltip title="Copy the built-in city→RA mappings onto your City docs so they show here and become editable.">
            <Button
              icon={<ThunderboltOutlined />}
              onClick={handleBackfill}
              loading={backfilling}
            >
              Import hardcoded
            </Button>
          </Tooltip>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap align="start" style={{ width: '100%' }}>
            <Select
              showSearch
              placeholder="1. Pick a city"
              style={{ minWidth: 240 }}
              value={selectedCityId}
              onChange={handlePickCity}
              options={cityOptions}
              filterOption={(input, option) =>
                (option?.label || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              allowClear
            />
            <Input
              placeholder="2. Search RA area name"
              style={{ width: 220 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
            <Button
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={searching}
            >
              Search RA
            </Button>
          </Space>

          {candidates.length > 0 && (
            <Space wrap align="center">
              <Text type="secondary">3. Pick the match:</Text>
              <Select
                placeholder="RA area"
                style={{ minWidth: 280 }}
                value={selectedAreaId}
                onChange={setSelectedAreaId}
                options={candidates.map((a) => ({
                  value: a.id,
                  label: `${a.name}${a.countryName ? ` — ${a.countryName}` : ''} (#${a.id})`,
                }))}
              />
              <Button
                type="primary"
                onClick={handleSave}
                loading={saving}
                disabled={!selectedAreaId || !selectedCityId}
              >
                Save mapping & enable
              </Button>
            </Space>
          )}

          {selectedCity?.raAreaId && (
            <Paragraph type="warning" style={{ margin: 0 }}>
              {selectedCity.name} is already mapped to{' '}
              {selectedCity.raAreaName} (#{selectedCity.raAreaId}). Saving will
              overwrite it.
            </Paragraph>
          )}
        </Space>
      </Card>

      <Card
        size="small"
        title={`Cities (${tableData.length}${showAll ? '' : ` of ${cities.length}`})`}
        extra={
          <Space>
            <Text type="secondary">Show all</Text>
            <Switch size="small" checked={showAll} onChange={setShowAll} />
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={load}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        {tableData.length === 0 && !loading ? (
          <Empty
            description={
              showAll ? 'No cities' : 'No mapped or enabled cities yet'
            }
          />
        ) : (
          <Table
            size="small"
            rowKey="_id"
            loading={loading}
            dataSource={tableData}
            columns={columns}
            pagination={{ pageSize: 20, showSizeChanger: true }}
          />
        )}
      </Card>
    </div>
  );
};

export default RaCitiesTab;
